import { useMemo } from 'react';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/contexts/AuthContext';
import { useAssetFinancials } from '@/hooks/useAssetFinancials';
import { useTotalWorkCosts, useAssetWorkCosts } from '@/hooks/useAssetWorkCosts';
import { useTotalSubscriptionCost } from '@/hooks/useAssetSubscriptionCosts';
import { useAssetDowntime } from '@/hooks/useAssetDowntime';
import { useAssetUsageSummary } from '@/hooks/useAssetUsage';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { DollarSign, TrendingDown, Activity, TrendingUp } from 'lucide-react';

interface AssetFinancialSummaryCardProps {
  assetId: string;
}

function fmt(n: number, opts?: Intl.NumberFormatOptions): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: 0, ...opts });
}

/**
 * Manager-only consolidated financial intelligence card.
 * Self-gates — returns null if user is not manager/owner.
 */
export function AssetFinancialSummaryCard({ assetId }: AssetFinancialSummaryCardProps) {
  const { isManager, isOwner } = usePermissions();
  const { activeShop } = useAuth();
  const laborRate = activeShop?.labor_rate ?? 150;

  const { data: financials } = useAssetFinancials(assetId);
  const { total: maintenanceCostLifetime } = useTotalWorkCosts(assetId);
  const { data: workCosts } = useAssetWorkCosts(assetId);
  const monthlySubscriptions = useTotalSubscriptionCost(assetId).total;
  const { data: downtimeEvents } = useAssetDowntime(assetId);
  const { summary: usage } = useAssetUsageSummary(assetId);

  // YTD maintenance cost
  const yearStart = useMemo(() => new Date(new Date().getFullYear(), 0, 1), []);
  const maintenanceCostYTD = useMemo(() => {
    if (!workCosts) return 0;
    return workCosts
      .filter((c) => c.log && new Date(c.log.performed_at) >= yearStart)
      .reduce((sum, c) => sum + Number(c.cost), 0);
  }, [workCosts, yearStart]);

  // Total downtime hours (lifetime, closed events + active running)
  const downtimeHoursLifetime = useMemo(() => {
    if (!downtimeEvents) return 0;
    let total = 0;
    for (const e of downtimeEvents) {
      const end = e.ended_at ? new Date(e.ended_at).getTime() : Date.now();
      total += (end - new Date(e.started_at).getTime()) / 3_600_000;
    }
    return total;
  }, [downtimeEvents]);

  if (!isManager && !isOwner) return null;

  // Loss side
  const downtimeCost = downtimeHoursLifetime * laborRate;

  // Usage side
  const jobsTouched = usage.usageCount;
  const laborSoldHours = usage.totalBookMinutes / 60;
  const revenueTouched = laborSoldHours * laborRate;

  // Total cost & rough ROI
  const annualSubscriptions = monthlySubscriptions * 12;
  const totalLifetimeCost =
    (Number(financials?.purchase_cost) || 0) +
    maintenanceCostLifetime +
    annualSubscriptions; // annualized as a rough proxy
  const roi = totalLifetimeCost > 0 ? revenueTouched / totalLifetimeCost : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <DollarSign className="h-4 w-4" />
          Financial Intelligence
        </CardTitle>
        <CardDescription>Manager-only · cost vs. loss vs. usage</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Cost */}
        <section>
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
            <h4 className="text-sm font-medium">Cost</h4>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">Purchase</p>
              <p className="font-medium">
                {financials?.purchase_cost ? `$${fmt(Number(financials.purchase_cost))}` : '—'}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Maintenance (lifetime)</p>
              <p className="font-medium">${fmt(maintenanceCostLifetime)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Maintenance (YTD)</p>
              <p className="font-medium">${fmt(maintenanceCostYTD)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Subscriptions</p>
              <p className="font-medium">
                ${fmt(monthlySubscriptions)}<span className="text-xs text-muted-foreground">/mo</span>
                {' · '}
                ${fmt(annualSubscriptions)}<span className="text-xs text-muted-foreground">/yr</span>
              </p>
            </div>
          </div>
        </section>

        <Separator />

        {/* Loss */}
        <section>
          <div className="flex items-center gap-2 mb-2">
            <TrendingDown className="h-3.5 w-3.5 text-muted-foreground" />
            <h4 className="text-sm font-medium">Loss</h4>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">Downtime hours</p>
              <p className="font-medium">{downtimeHoursLifetime.toFixed(1)}h</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Downtime cost</p>
              <p className="font-medium">${fmt(downtimeCost)}</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Computed at shop labor rate (${laborRate}/h).
          </p>
        </section>

        <Separator />

        {/* Usage */}
        <section>
          <div className="flex items-center gap-2 mb-2">
            <Activity className="h-3.5 w-3.5 text-muted-foreground" />
            <h4 className="text-sm font-medium">Usage</h4>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">Jobs touched</p>
              <p className="font-medium">{jobsTouched}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Labor sold through asset</p>
              <p className="font-medium">{laborSoldHours.toFixed(1)}h · ${fmt(revenueTouched)}</p>
            </div>
          </div>
        </section>

        {roi !== null && totalLifetimeCost > 0 && (
          <>
            <Separator />
            <section className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              <p className="text-sm">
                <span className="text-muted-foreground">Rough ROI:</span>{' '}
                <span className="font-semibold">{roi.toFixed(2)}×</span>
                <span className="text-xs text-muted-foreground ml-2">
                  (revenue touched ÷ purchase + maintenance + 1y subs)
                </span>
              </p>
            </section>
          </>
        )}
      </CardContent>
    </Card>
  );
}
